import {v4} from 'uuid';
import {select, put, call} from 'redux-saga/effects';
import {service as auditLogService} from 'src/services/audit_log';
import {service as configService} from 'src/services/config';
import {TYPE} from 'src/types/pipeline';
import {
  PIPELINE_DELETE,
  PIPELINE_SAVE,
  PIPELINE_SET_APPLY,
  PIPELINE_TOGGLE_APPLY
} from './types';
import {drop, setPipeline} from './actions';
import {defaults} from './reducer';
import {get, create, update, remove} from './api';

// reduce pipes size for efficient storing
export const compressPipes = pipes => pipes.map(({ type, logic, fields }) => {
  const pipe = { type };

  if (type === TYPE.MATCH) {
    pipe.logic = logic;
  }

  pipe.fields = fields.map(({ clause, code, is, sort, type }) => {
    const field = { code };

    switch (pipe.type) {
      case TYPE.SORT:
        field.sort = sort;
        break;
      case TYPE.MATCH:
        field.is = is;
        field.type = type;
        field.clause = clause;
        break;
      default:
        break;
    }

    return field;
  });

  return pipe;
});

// restore serialized pipes
export const decompressPipes = pipes => pipes.map(({
  type,
  logic = defaults.pipe.logic,
  fields,
}) => ({
  uuid: v4(),
  type,
  logic,
  fields: fields.map(({
    code,
    is = defaults.field.is,
    type = defaults.field.type,
    sort = defaults.field.sort,
    clause = defaults.field.clause,
  }) => ({
    uuid: v4(),
    code,
    is,
    type,
    sort,
    clause,
  })),
}));

export default (flows) => {
  function* pipelineApply(applied) {
    if (applied) {
      const pipes = yield select(state => state.pipelines.current.pipes);
      yield put(auditLogService.getFlow('send').request({
        payload: { query: pipes.toJS(), action: 'query' },
      }));
    } else {
      yield put(auditLogService.getFlow('send').request({
        payload: { query: [], action: 'query' },
      }));
    }
  }

  function* pipelineSetApply({ applied }) {
    yield pipelineApply(applied);
  }

  function* pipelineToggleApply() {
    const applied = yield select(state => state.pipelines.current.applied);
    yield pipelineApply(applied);
  }

  function* pipelineSave({ id, name }) {
    try {
      const { config, pipes } = yield select(state => ({
        config: state.config.get || {},
        pipes: state.pipelines.current.pipes.toJS(),
      }));

      const uuid = id || v4();
      config.pipelines = {
        ...config.pipelines,
        [uuid]: {
          id: uuid,
          name,
          pipes,
        }
      };

      yield put(configService.getFlow('setManager').request(config));
      yield put({ type: PIPELINE_SAVE, payload: config.pipelines[uuid] });
    } catch (e) {
      console.error(e);
    }
  }

  function* pipelineDelete({ id }) {
    try {
      const { config, pipelineId } = yield select(state => ({
        config: state.config.get || {},
        pipelineId: state.pipelines.current.id,
      }));
      const pipelines = config.pipelines;
      delete pipelines[id];

      config.pipelines = { ...pipelines };

      yield put(configService.getFlow('setManager').request(config));

      if (id === pipelineId) {
        yield put(drop());
      }
    } catch (e) {
      console.error(e);
    }
  }

  flows.watch(PIPELINE_SET_APPLY, pipelineSetApply);
  flows.watch(PIPELINE_TOGGLE_APPLY, pipelineToggleApply);
  flows.watch(PIPELINE_SAVE, pipelineSave);
  // flows.watch(PIPELINE_DELETE, pipelineDelete());

  flows.getFlow('get').setActions({
    onFetch: function* () {
      const userId = yield select(state => state.user.email);
      const { data } = yield call(() => get());

      return data.map(({ id, is_shared, owner_id, serialized_pipeline }) => {
        const { name, pipes } = JSON.parse(serialized_pipeline);

        return {
          id,
          name,
          isShared: is_shared,
          ownerId: owner_id,
          isOwn: owner_id === userId,
          pipes: decompressPipes(pipes),
        };
      });
    }
  });

  flows.getFlow('save').setActions({
    onFetch: function* ({ id, name, isShared }) {
      const { pipeline, list } = yield select(state => ({
        pipeline: state.pipelines.current,
        list: state.pipelines.get.data.slice(),
      }));

      let savingPipeline = null;
      let is_shared = isShared || false;

      for (let i in list) {
        if (id && list[i].id === id) {
          savingPipeline = list[i];

          if (pipeline.id === savingPipeline.id) {
            savingPipeline.pipes = pipeline.pipes.toJS();
          }

          savingPipeline.name = name;

          if (isShared != null) {
            savingPipeline.isShared = isShared;
          }

          is_shared = savingPipeline.isShared;
          break;
        }

        if (list[i].name.toUpperCase() === name.toUpperCase()) {
          throw new Error(
            `A Pipeline with name "${name}" is already exists. Please specify another name.`
          );
        }
      }

      const payload = {
        name,
        pipes: compressPipes(pipeline.pipes.toJS()),
      };

      const { data } = yield call(() => id
        ? update(id, payload, { is_shared })
        : create(payload, { is_shared })
      );

      yield put(setPipeline({
        id: data || id,
        name,
        isShared: is_shared,
      }));

      if (savingPipeline) {
        yield put(flows.getFlow('get').setData(list));
      } else {
        yield put(flows.getFlow('get').request());
      }
    }
  });

  flows.getFlow('remove').setActions({
    onFetch: function* ({ id }) {
      yield call(() => remove(id));

      const { pipeline, list } = yield select(state => ({
        pipeline: state.pipelines.current,
        list: state.pipelines.get.data,
      }));

      if (pipeline.id === id) {
        yield put(drop());
      }

      yield put(flows.getFlow('get').setData(list.filter(item => item.id !== id)));
    }
  });

  return flows.getSagas();
};
